/**
 * ServiceOrderCalendarModal - Calendário de agendamentos de OS (BR-OS-031)
 *
 * Preset do `CalendarModal` genérico: sem menu lateral, só a fonte "Ordem de
 * Serviço", filtros por Prestador e Tipo de serviço e eventos coloridos pela
 * cor do tipo de serviço. Filtros usam os metadados que o trigger de sincronia
 * já grava no evento (`attendees.provider_person_id` / `service_type_id`).
 *
 * RELEASE: 2026-10-16 v1.1.0 - Botão "Nova OS" e clique/arraste no grid abrem o
 *          modal de criação de OS (ServiceOrderFormDialog) com o início pré-preenchido
 *          pelo slot escolhido, em vez do modal de evento manual do calendário.
 * RELEASE: 2026-10-10 v1.0.0 - Versão inicial.
 */
import { useCallback, useMemo, useState } from 'react';
import { CalendarModal } from '@/components/calendar/CalendarModal';
import { ServiceOrderFormDialog } from '@/components/service-orders/ServiceOrderFormDialog';
import { PersonSelector } from '@/components/persons/PersonSelector';
import { Button } from '@/components/ui/button';
import { useServiceTypes } from '@/hooks/useServiceTypes';
import type { UnifiedCalendarEvent } from '@/hooks/useCalendarUnifiedEvents';
import { QUICK_FILTER_PILL } from '@/components/layout/list-page/quickFilterStyles';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Data inicial (ex.: início do agendamento clicado) */
  initialDate?: string | null;
  /** Id da OS a destacar no calendário */
  focusServiceOrderId?: string | null;
  /** Prestador pré-selecionado no filtro */
  initialProviderId?: string | null;
}

/** Lê metadados da OS gravados no evento */
const meta = (e: UnifiedCalendarEvent) => (e.attendees && typeof e.attendees === 'object' && !Array.isArray(e.attendees)
  ? (e.attendees as Record<string, unknown>) : {});

const SOURCES = ['service_order'] as const;

export function ServiceOrderCalendarModal({ open, onOpenChange, initialDate, focusServiceOrderId, initialProviderId }: Props) {
  const [providerId, setProviderId] = useState<string | null>(null);
  const [typeIds, setTypeIds] = useState<string[]>([]);
  // v1.1.0: slot escolhido no grid (null = criação pelo botão, sem horário)
  const [createSlot, setCreateSlot] = useState<{ start: Date; end: Date; allDay: boolean } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const { serviceTypes: types } = useServiceTypes(true);
  const colorByType = useMemo(() => new Map(types.map((t) => [t.id, t.color])), [types]);

  // Criação de OS a partir da agenda: botão (slot null) ou clique/arraste no grid
  const handleCreateRequest = useCallback((slot: { start: Date; end: Date; allDay: boolean } | null) => {
    setCreateSlot(slot);
    setCreateOpen(true);
  }, []);

  // Filtro aplicado no cliente sobre os eventos já autorizados pela RPC
  const eventFilter = useCallback((e: UnifiedCalendarEvent) => {
    if (e.source_type !== 'service_order') return false;
    const m = meta(e);
    if (providerId && m.provider_person_id !== providerId) return false;
    if (typeIds.length && !typeIds.includes(String(m.service_type_id ?? ''))) return false;
    return true;
  }, [providerId, typeIds]);

  const eventColor = useCallback((e: UnifiedCalendarEvent) => colorByType.get(String(meta(e).service_type_id ?? '')), [colorByType]);

  const toggleType = (id: string) => setTypeIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <>
    <CalendarModal
      open={open}
      onOpenChange={(o) => { if (o) setProviderId(initialProviderId ?? null); onOpenChange(o); }}
      title="Agenda de Ordens de Serviço"
      hideSidebar
      forcedSources={[...SOURCES]}
      initialDate={initialDate ?? undefined}
      initialView={initialDate ? 'timeGridDay' : undefined}
      eventFilter={eventFilter}
      eventColor={eventColor}
      focusEventId={focusServiceOrderId ?? null}
      createLabel="Nova OS"
      onCreateRequest={handleCreateRequest}
      toolbarExtra={
        <>
          <div className="w-64">
            <PersonSelector role="service_provider" value={providerId} onSelect={setProviderId} placeholder="Todos os prestadores" />
          </div>
          <Button size="sm" variant="outline" className={cn('h-8', QUICK_FILTER_PILL, !typeIds.length && 'ring-1 ring-primary')}
            onClick={() => setTypeIds([])}>Todos os tipos</Button>
          {types.map((t) => (
            <Button key={t.id} size="sm" variant="outline" onClick={() => toggleType(t.id)}
              className={cn('h-8 gap-1.5', QUICK_FILTER_PILL, typeIds.includes(t.id) && 'ring-1 ring-primary')}>
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: t.color }} />{t.name}
            </Button>
          ))}
        </>
      }
    />
    {/* v1.1.0: criação de OS com o início pré-preenchido pelo slot do grid */}
    <ServiceOrderFormDialog
      open={createOpen}
      onOpenChange={setCreateOpen}
      serviceOrderId={null}
      defaultScheduledStart={createSlot ? createSlot.start.toISOString() : null}
    />
    </>
  );
}
